import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LinkIcon from '@mui/icons-material/Link';
import { DialogHeader, DialogFooter } from '../common/AppDialog';

const ACCOUNT_TYPES = ['checking', 'savings', 'credit', 'investment', 'loan', 'other'];
const CREATE_NEW = '__CREATE_NEW__';

const AccountRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
  transition: 'border-color 0.2s',
  '&:hover': {
    borderColor: theme.palette.primary.light,
  },
}));

const AccountMappingDialog = ({ open, onClose, onConfirm, unknownAccounts, existingAccounts, autoResolvedCount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State: mapping for each unknown account
  // { [accountName]: { action: 'CREATE_NEW' | 'MAP', targetId?: string, type?: string, remember: boolean } }
  const [mappings, setMappings] = useState(() => {
    const initial = {};
    for (const entry of unknownAccounts) {
      initial[entry.name] = {
        action: 'CREATE_NEW',
        type: 'checking',
        remember: false,
      };
    }
    return initial;
  });

  const handleActionChange = (accountName, value) => {
    setMappings(prev => {
      if (value === CREATE_NEW) {
        return {
          ...prev,
          [accountName]: { action: 'CREATE_NEW', type: 'checking', remember: false },
        };
      }
      const target = existingAccounts.find(a => a.id === value);
      return {
        ...prev,
        [accountName]: {
          action: 'MAP',
          targetId: value,
          targetName: target?.name || '',
          remember: true,
        },
      };
    });
  };

  const handleTypeChange = (accountName, type) => {
    setMappings(prev => ({
      ...prev,
      [accountName]: { ...prev[accountName], type },
    }));
  };

  const handleRememberChange = (accountName, checked) => {
    setMappings(prev => ({
      ...prev,
      [accountName]: { ...prev[accountName], remember: checked },
    }));
  };

  const createNewCount = useMemo(
    () => Object.values(mappings).filter(m => m.action === 'CREATE_NEW').length,
    [mappings]
  );
  const mapExistingCount = useMemo(
    () => Object.values(mappings).filter(m => m.action === 'MAP').length,
    [mappings]
  );

  const handleConfirm = () => {
    onConfirm(mappings);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { overflow: 'hidden' } }}
    >
      <DialogHeader title="Unknown Accounts Found" onClose={onClose} />

      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The following account names from your import don't match any existing accounts.
          For each one, choose to map it to an existing account or create a new one.
        </Typography>

        {autoResolvedCount > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {autoResolvedCount} account{autoResolvedCount > 1 ? 's were' : ' was'} automatically resolved from saved mappings.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {createNewCount > 0 && (
            <Chip
              icon={<AddCircleOutlineIcon />}
              label={`${createNewCount} new`}
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {mapExistingCount > 0 && (
            <Chip
              icon={<LinkIcon />}
              label={`${mapExistingCount} mapped`}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {unknownAccounts.map(({ name, transactionCount, displayName }) => {
            const mapping = mappings[name];
            return (
              <AccountRow key={name}>
                {/* Header: account name + transaction count */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                      {displayName || `"${name}"`}
                    </Typography>
                    {displayName && (
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {name}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${transactionCount} txn${transactionCount !== 1 ? 's' : ''}`}
                    size="small"
                    color="default"
                    sx={{ flexShrink: 0 }}
                  />
                </Box>

                {/* Action dropdown */}
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={mapping?.action === 'MAP' ? mapping.targetId : CREATE_NEW}
                    onChange={(e) => handleActionChange(name, e.target.value)}
                    label="Action"
                  >
                    <MenuItem value={CREATE_NEW}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddCircleOutlineIcon fontSize="small" color="info" />
                        Create New Account
                      </Box>
                    </MenuItem>
                    {existingAccounts.length > 0 && <Divider />}
                    {existingAccounts.map(acc => (
                      <MenuItem key={acc.id} value={acc.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: acc.color || theme.palette.grey[400],
                              flexShrink: 0,
                            }}
                          />
                          {acc.name}
                          <Chip label={acc.type} size="small" variant="outlined" sx={{ ml: 'auto', height: 20, fontSize: '0.75rem' }} />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Account type selector for Create New */}
                {mapping?.action === 'CREATE_NEW' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={mapping.type || 'checking'}
                      onChange={(e) => handleTypeChange(name, e.target.value)}
                      label="Account Type"
                    >
                      {ACCOUNT_TYPES.map(type => (
                        <MenuItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Remember checkbox */}
                {mapping?.action === 'MAP' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mapping.remember ?? true}
                        onChange={(e) => handleRememberChange(name, e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="caption" color="text.secondary">
                        Remember this mapping for future imports
                      </Typography>
                    }
                  />
                )}
              </AccountRow>
            );
          })}
        </Box>
      </DialogContent>

      <DialogFooter
        layout="inline"
        secondaryLabel="Cancel Import"
        onSecondary={onClose}
        primaryLabel="Continue Import"
        onPrimary={handleConfirm}
      />
    </Dialog>
  );
};

export default AccountMappingDialog;
